import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, Loader2, ImageOff } from 'lucide-react';
import { fmtDate } from './fleetUtils';

const fetchBlob = async (pid) => {
  const res = await axios.get(`${API}/fleet/photos/${pid}`, { withCredentials: true, responseType: 'blob' });
  return { url: URL.createObjectURL(res.data), type: res.headers['content-type'] || res.data.type };
};

const Thumb = ({ pid, onClick }) => {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [info, blob] = await Promise.all([
          axios.get(`${API}/fleet/photos/${pid}/info`, { withCredentials: true }).then(r => r.data).catch(() => ({})),
          fetchBlob(pid),
        ]);
        if (alive) setState({ url: blob.url, isPdf: (blob.type || '').includes('pdf') || (info.content_type || '').includes('pdf'), info });
      } catch (e) {
        if (alive) setState({ error: e.response?.status === 404 ? 'introuvable' : 'refusé' });
      }
    })();
    return () => { alive = false; };
  }, [pid]);
  if (state.loading) return <div className="w-20 h-20 rounded border flex items-center justify-center bg-gray-50"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>;
  if (state.error) return (
    <div className="w-20 h-20 rounded border flex flex-col items-center justify-center bg-gray-50 text-gray-400" title="Ce fichier est introuvable ou n'est plus disponible.">
      <ImageOff className="w-5 h-5" /><span className="text-[9px] mt-1">{state.error}</span>
    </div>
  );
  if (state.isPdf) return (
    <button type="button" onClick={() => onClick(state)} className="w-20 h-20 rounded border flex flex-col items-center justify-center bg-red-50 hover:bg-red-100" data-testid={`attachment-pdf-${pid}`}>
      <FileText className="w-6 h-6 text-red-600" /><span className="text-[9px] mt-1 truncate max-w-[70px]">{state.info?.original_filename || 'PDF'}</span>
    </button>
  );
  return <img src={state.url} alt={state.info?.original_filename || 'document'} onClick={() => onClick(state)}
    className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80" data-testid={`attachment-img-${pid}`} />;
};

export const AttachmentGallery = ({ photoIds }) => {
  const [viewer, setViewer] = useState(null);
  const [zoom, setZoom] = useState(1);
  const ids = photoIds || [];
  const openAt = useCallback((idx) => { setZoom(1); setViewer({ idx }); }, []);
  if (ids.length === 0) return null;

  const ViewerBody = () => {
    const pid = ids[viewer.idx];
    const [state, setState] = useState(null);
    useEffect(() => {
      let alive = true;
      setState(null);
      (async () => {
        try {
          const [info, blob] = await Promise.all([
            axios.get(`${API}/fleet/photos/${pid}/info`, { withCredentials: true }).then(r => r.data).catch(() => ({})),
            fetchBlob(pid),
          ]);
          if (alive) setState({ ...blob, info, isPdf: (blob.type || '').includes('pdf') });
        } catch (e) {
          if (alive) setState({ error: true, status: e.response?.status });
        }
      })();
      return () => { alive = false; };
    }, [pid]);
    if (!state) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    if (state.error) return <p className="p-8 text-center text-gray-500" data-testid="attachment-missing">Ce fichier est introuvable ou n'est plus disponible.</p>;
    const dl = () => {
      const a = document.createElement('a');
      a.href = state.url; a.download = state.info?.original_filename || pid; a.click();
    };
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
          <span>{state.info?.original_filename || pid} · {state.info?.uploaded_by_name || '—'} · {fmtDate(state.info?.created_at)}</span>
          <div className="flex gap-1">
            {!state.isPdf && <>
              <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.min(z + 0.5, 4))} data-testid="viewer-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.max(z - 0.5, 1))}><ZoomOut className="w-4 h-4" /></Button>
            </>}
            <Button size="sm" variant="outline" onClick={dl} data-testid="viewer-download"><Download className="w-4 h-4 mr-1" />Télécharger</Button>
          </div>
        </div>
        {state.isPdf ? (
          <div className="text-center space-y-2 py-6">
            <FileText className="w-12 h-12 mx-auto text-red-600" />
            <Button onClick={() => window.open(state.url, '_blank')} data-testid="viewer-open-pdf">Consulter le document</Button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[65vh] flex justify-center bg-gray-900/5 rounded">
            <img src={state.url} alt="pièce jointe" style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
              className="max-h-[65vh] transition-transform" data-testid="viewer-image" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {ids.map((pid) => <Thumb key={pid} pid={pid} onClick={() => openAt(ids.indexOf(pid))} />)}
      </div>
      {viewer && (
        <Dialog open onOpenChange={(o) => !o && setViewer(null)}>
          <DialogContent className="max-w-3xl" data-testid="attachment-viewer">
            <DialogHeader><DialogTitle>Pièce jointe {viewer.idx + 1} / {ids.length}</DialogTitle></DialogHeader>
            <ViewerBody />
            {ids.length > 1 && (
              <div className="flex justify-between">
                <Button variant="outline" size="sm" disabled={viewer.idx === 0} onClick={() => { setZoom(1); setViewer({ idx: viewer.idx - 1 }); }} data-testid="viewer-prev"><ChevronLeft className="w-4 h-4" /> Précédente</Button>
                <Button variant="outline" size="sm" disabled={viewer.idx === ids.length - 1} onClick={() => { setZoom(1); setViewer({ idx: viewer.idx + 1 }); }} data-testid="viewer-next">Suivante <ChevronRight className="w-4 h-4" /></Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AttachmentGallery;
